// Site: ligar e desligar partes do site público (secções da página inicial, menu, descargas e os ficheiros delas).
// Toda a equipa vê; muda quem tem "site.edit". Cada gravação vai para a auditoria com o antes e o depois.
// Lista, valores por omissão e ficheiros de cada parte: @ecliptica/config/site-sections.
import { useApi } from "@ecliptica/api-client/react";
import { SITE_SECTIONS, type SiteSection, type SiteSections } from "@ecliptica/config/site-sections";
import { useTranslation } from "@ecliptica/i18n";
import { Alert, Badge, Button, Card, Input, Label, PageHeader, Skeleton, Switch, toast } from "@ecliptica/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useCan } from "../../features/auth/RequireStaff";

export function SitePage() {
  const { t } = useTranslation();
  const api = useApi();
  const qc = useQueryClient();
  const can = useCan();
  const editable = can("site.edit");
  const saved = useQuery({
    queryKey: ["site-sections"],
    queryFn: async () => {
      const res = await api.v1.admin.site.sections.$get();
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return (await res.json()).sections;
    },
  });

  const [draft, setDraft] = useState<SiteSections | null>(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (saved.data) setDraft(saved.data);
  }, [saved.data]);

  if (!saved.data || !draft) return <Skeleton className="h-96 w-full max-w-3xl" />;
  const changed = SITE_SECTIONS.filter((k) => draft[k] !== saved.data[k]);

  async function save() {
    if (!draft) return;
    setBusy(true);
    const patch = Object.fromEntries(changed.map((k) => [k, draft[k]])) as Partial<Record<SiteSection, boolean>>;
    const res = await api.v1.admin.site.sections.$put({ json: { sections: patch, reason: reason || undefined } });
    setBusy(false);
    if (!res.ok) return toast.error(t("error.generic"));
    toast.success(t("site.saved"));
    setReason("");
    await qc.invalidateQueries({ queryKey: ["site-sections"] });
    await qc.invalidateQueries({ queryKey: ["audit"] });
  }

  return (
    <>
      <PageHeader title={t("site.title")} description={t("site.description")} />
      <div className="flex max-w-3xl flex-col gap-4">
        {!editable && <Alert variant="info">{t("site.readOnly")}</Alert>}
        <Card className="divide-y">
          {SITE_SECTIONS.map((k) => (
            <div key={k} className="flex items-start gap-4 p-4 sm:p-5">
              <div className="min-w-0 flex-1">
                <Label htmlFor={`section-${k}`} className="flex items-center gap-2 text-base font-semibold">
                  {t(`site.parts.${k}.label`)}
                  <Badge variant={draft[k] ? "success" : "secondary"}>{t(draft[k] ? "site.on" : "site.off")}</Badge>
                  {draft[k] !== saved.data[k] && <Badge variant="warning">{t("site.unsaved")}</Badge>}
                </Label>
                <p className="mt-1 text-sm text-muted-foreground">{t(`site.parts.${k}.hint`)}</p>
              </div>
              <Switch
                id={`section-${k}`}
                checked={draft[k]}
                disabled={!editable || busy}
                onCheckedChange={(on) => setDraft((d) => (d ? { ...d, [k]: on } : d))}
                aria-label={t(`site.parts.${k}.label`)}
              />
            </div>
          ))}
        </Card>
        <p className="text-sm text-muted-foreground">{t("site.applyHint")}</p>
        {editable && (
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="reason">{t("site.reason")}</Label>
              <Input id="reason" maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} />
            </div>
            <div className="flex gap-2">
              <Button disabled={!changed.length || busy} onClick={() => void save()}>
                {t("action.save")}
              </Button>
              <Button variant="outline" disabled={!changed.length || busy} onClick={() => setDraft(saved.data)}>
                {t("site.discard")}
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
